import { z } from 'zod';
import { getDistance } from 'ol/sphere.js';
import type { DataFeature } from '../shared/data-layer.js';
import { isNumber, isSchemaRecord, isString } from '../shared/runtime-values.js';

export interface RoutePoint {
  coordinate: [number, number];
  distance: number;
  elevation: number | null;
  time: number | null;
  segment: number;
}

export interface RouteProfileData {
  points: RoutePoint[];
  distance: number;
  ascent: number | null;
  descent: number | null;
  elapsed: number | null;
  start: number | null;
}

export function routeProfileData(feature: DataFeature): RouteProfileData | null {
  const parsed = routeSegments(feature);
  if (!parsed) return null;
  const { segments, times, multi } = parsed;
  const points: RoutePoint[] = [];
  let distance = 0;
  let completeTime = true;
  for (const [segment, coordinates] of segments.entries()) {
    const segmentTimes = multi ? times[segment] : times;
    let previous: RoutePoint | undefined;
    for (const [index, position] of coordinates.entries()) {
      const coordinate: [number, number] = [position[0], position[1]];
      const elevation = isNumber(position[2]) && Number.isFinite(position[2]) ? position[2] : null;
      const time = pointTime(segmentTimes, index);
      if (time === null || (points.length && time <= (points.at(-1)?.time ?? -Infinity))) completeTime = false;
      if (previous) distance += getDistance(previous.coordinate, coordinate);
      const point = { coordinate, elevation, time, distance, segment };
      points.push(point);
      previous = point;
    }
  }
  if (points.length < 2) return null;
  const start = points[0].time;
  const end = points.at(-1)!.time;
  return { points, distance, ...routeElevation(points),
    elapsed: completeTime ? elapsedSeconds(start, end) : null, start };
}

export function routeDuration(seconds: number): string {
  const total = Math.round(seconds);
  return `${Math.floor(total / 3600)}:${String(Math.floor(total / 60) % 60).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

function pointTime(times: import('../shared/schema-input.js').SchemaInput, index: number): number | null {
  const value = Array.isArray(times) ? times[index] : null;
  const time = isString(value) ? Date.parse(value) : NaN;
  return Number.isFinite(time) ? time : null;
}

const segmentsSchema = z.array(z.array(z.array(z.number()).min(2)).min(2));
export function routeSegments(feature: DataFeature) {
  const geometry = feature.geometry;
  if (!geometry || (geometry.type !== 'LineString' && geometry.type !== 'MultiLineString')) return null;
  const multi = geometry.type === 'MultiLineString';
  const result = segmentsSchema.safeParse(multi ? geometry.coordinates : [geometry.coordinates]);
  if (!result.success) return null;
  const properties = feature.properties.coordinateProperties;
  const times = isSchemaRecord(properties) && Array.isArray(properties.times) ? properties.times : [];
  return { segments: result.data, times, multi };
}

function elapsedSeconds(start: number | null, end: number | null): number | null {
  return start !== null && end !== null && end > start ? (end - start) / 1000 : null;
}

function routeElevation(points: readonly RoutePoint[]) {
  if (points.some((point) => point.elevation === null)) return { ascent: null, descent: null };
  let ascent = 0; let descent = 0;
  let anchor = points[0]?.elevation ?? 0;
  let extreme = anchor; let direction = 0; let segment = points[0]?.segment;
  const finish = () => { ascent += Math.max(0, extreme - anchor); descent += Math.max(0, anchor - extreme); };
  for (const point of points) {
    const elevation = point.elevation!;
    if (point.segment !== segment) { finish(); anchor = elevation; extreme = elevation; direction = 0; segment = point.segment; }
    if (direction === 0) {
      if (Math.abs(elevation - anchor) >= 5) { direction = Math.sign(elevation - anchor); extreme = elevation; }
    } else if ((elevation - extreme) * direction >= 0) extreme = elevation;
    else if (Math.abs(elevation - extreme) >= 5) { finish(); anchor = extreme; extreme = elevation; direction *= -1; }
  }
  finish();
  return { ascent, descent };
}
